// src/components/Footer/RedesignedFooter.jsx
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Github, Linkedin, Mail, ArrowUp, Youtube } from 'lucide-react';
import { Link } from 'react-router-dom';
import HeartEasterEgg from '../EasterEggs/HeartEasterEgg';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

// Styled components
const FooterWrapper = styled.footer`
  position: relative;
  padding: 5rem 0 2rem;
  background: ${({ theme }) => `linear-gradient(to bottom, ${theme.colors.bg}, ${theme.colors.card})`};
  overflow: hidden;
`;

const FooterWave = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 80px;
  background: ${({ theme }) => `${theme.colors.accent}10`};
  clip-path: polygon(
    0% 0%,
    100% 0%,
    100% 100%,
    0% 0%
  );
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  @media (min-width: 768px) {
    grid-template-columns: 1.5fr 1fr 1fr;
  }
`;

const FooterSection = styled.div``;

const FooterLogo = styled.div`
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const FooterText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 350px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: white;
    transform: translateY(-5px);
  }
`;

const FooterTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.5rem;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: inline-block;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    transform: translateX(5px);
  }
`;

const ExternalFooterLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: inline-block;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    transform: translateX(5px);
  }
`;

const FooterDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.secondary};
  margin: 3rem 0 2rem;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #ef4444;
  }
`;

const FooterNav = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const FooterNavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
  transform: ${({ $isVisible }) => $isVisible ? 'translateY(0)' : 'translateY(20px)'};
  pointer-events: ${({ $isVisible }) => $isVisible ? 'all' : 'none'};
  z-index: 90;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    animation: ${float} 2s infinite ease-in-out;
  }
`;

const RedesignedFooter = () => {
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <FooterWrapper>
      <FooterWave />
      <Container>
        <FooterContent>
          <FooterSection>
            <FooterLogo>
              <h3>Pascal Dietrich</h3>
            </FooterLogo>
            <FooterText>
              Software developer with a passion for clean code and test-driven development. Always learning and exploring new technologies.
            </FooterText>
            <SocialLinks>
              <SocialLink 
                href="https://github.com/pascalnd" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="GitHub Profile"
              >
                <Github size={20} />
              </SocialLink>
              <SocialLink 
                href="https://linkedin.com/in/pascalnd" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="LinkedIn Profile"
              >
                <Linkedin size={20} />
              </SocialLink>
              <SocialLink 
                href="https://www.youtube.com/@PascalNerD" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="YouTube Channel"
              >
                <Youtube size={20} />
              </SocialLink>
              <SocialLink 
                href="mailto:mail@pascalndietrich.de" 
                aria-label="Send Email"
              >
                <Mail size={20} />
              </SocialLink>
            </SocialLinks>
          </FooterSection>
          
          <FooterSection>
            <FooterTitle>Navigation</FooterTitle>
            <FooterLinks>
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/#about">About Me</FooterLink>
              <FooterLink to="/#learning">Currently Learning</FooterLink>
              <FooterLink to="/#projects">Projects</FooterLink>
              <FooterLink to="/#contact">Contact</FooterLink>
            </FooterLinks>
          </FooterSection>
          
          <FooterSection>
            <FooterTitle>Other Links</FooterTitle>
            <FooterLinks>
              <FooterLink to="/resume">Resume</FooterLink>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <ExternalFooterLink
                href="/survey.html"
                target="_blank" // Optional: Open in new tab
                rel="noopener noreferrer"
              >
                Survey Results
              </ExternalFooterLink>
              <ExternalFooterLink 
                href="https://github.com/pascalnd" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                GitHub Profile
              </ExternalFooterLink>
              <ExternalFooterLink 
                href="https://www.youtube.com/@PascalNerD" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                YouTube Channel
              </ExternalFooterLink>
            </FooterLinks>
          </FooterSection>
        </FooterContent>
        
        <FooterDivider />
        
        <FooterBottom>
          <Copyright>
            © {new Date().getFullYear()} Pascal Dietrich. Made with <HeartEasterEgg /> in Germany
          </Copyright>
          
          <FooterNav>
            <FooterNavLink to="/privacy">Privacy Policy</FooterNavLink>
            <FooterNavLink to="/resume">Resume</FooterNavLink>
          </FooterNav>
        </FooterBottom>
      </Container>
      
      <ScrollToTopButton 
        $isVisible={showScrollTop} 
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <ArrowUp size={20} />
      </ScrollToTopButton>
    </FooterWrapper>
  );
};

export default RedesignedFooter;